import { BaseButton, Button, IconButton, IStackItemProps, Label, mergeStyles, Stack, useTheme } from '@fluentui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IToggleOption } from "./IToggleOption.type";
import { DeepMerge } from 'global/Utility/TSFunctions';




export interface IToggleOptionsProps<T> {
    label?: string;
    options: IToggleOption<T>[];
    onChange?: (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null,
        option?: IToggleOption<T>
    ) => void
    defaultSelectedKey?: string | number | null;
    disabled?: boolean;
    clearDisabled?: boolean;
    styles?: IStackItemProps['styles'];

    /**
     * If true, once an option is selected, it cannot be unselected by clicking on it again. It can only be cleared by the clear button (if not disabled).
     */
    unclick?: boolean;

    /**
     * Hides the clear button.
     */
    clearHidden?: boolean;
    optionsClassName?: string;
}

const DEFAULT_ITEM_STYLE: IStackItemProps['styles'] = { root: { flexBasis: '0' } };
const ROOT_STYLE: React.CSSProperties = { minWidth: '178px' };
const OPTION_TEXT_STYLE: React.CSSProperties = { position: 'relative', userSelect: 'none' };
const SELECTED_ARROW_STYLE: React.CSSProperties = { position: 'absolute', left: '-17px' };
const CLEAR_CONTAINER_STYLE: React.CSSProperties = { width: '0px' };
const CLEAR_BUTTON_STYLE = { root: { height: '100%', right: '30px', top: '-30px' } };
const CLEAR_ICON_PROPS = { iconName: 'Clear', styles: { root: { fontSize: 'x-small', padding: '0px' } } };
const SEGMENTED_LEFT_STYLE = { borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' };
const SEGMENTED_MIDDLE_STYLE = { borderRadius: '0px', marginLeft: '-1px' };
const SEGMENTED_RIGHT_STYLE = { borderTopRightRadius: '8px', borderBottomRightRadius: '8px', marginLeft: '-1px' };


const BASE_STYLE = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '30px',
    cursor: 'pointer',
    border: '1px solid',
    transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
}

/**
 * A generic toggle options component.
 * @param props 
 * @returns 
 */
const ToggleOptionsControl = <T,>(props: IToggleOptionsProps<T>) => {
    const theme = useTheme();

    const [selectedKey, setSelectedKey] = useState<string | number | null>(props.defaultSelectedKey ?? null);

    useEffect(() => {
        setSelectedKey(props.defaultSelectedKey ?? null);
    }, [props.defaultSelectedKey]);

    useEffect(() => {
        if (selectedKey === null) {
            return;
        }

        const exists = props.options.some((option) => option.key === selectedKey);
        if (!exists) {
            setSelectedKey(null);
        }
    }, [props.options, selectedKey]);

    const selectedOption = useMemo(() => {
        if (selectedKey === null) {
            return undefined;
        }

        return props.options.find((option) => option.key === selectedKey);
    }, [props.options, selectedKey]);

    const optionClasses = useMemo(() => {
        const primaryBackground = theme.palette.themePrimary;
        const primaryBorder = theme.palette.themeDarkAlt;
        const primaryHoverBackground = theme.palette.themeDarkAlt;
        const secondaryText = theme.semanticColors.bodyText;
        const secondaryBackground = theme.palette.white;
        const secondaryBorder = theme.semanticColors.bodyFrameDivider;
        const secondaryHoverBackground = theme.semanticColors.menuItemBackgroundHovered;
        const disabledText = theme.palette.neutralTertiary;
        const disabledBackground = theme.palette.neutralLighter;
        const disabledBorder = theme.palette.neutralLight;

        const primaryClass = mergeStyles({
            ...BASE_STYLE,
            color: theme.palette.white,
            backgroundColor: primaryBackground,
            borderColor: primaryBorder,
            "&:hover": {
                backgroundColor: primaryHoverBackground,
                borderColor: primaryHoverBackground,
            }
        });

        const secondaryClass = mergeStyles({
            ...BASE_STYLE,
            color: secondaryText,
            backgroundColor: secondaryBackground,
            borderColor: secondaryBorder,
            "&:hover": {
                backgroundColor: secondaryHoverBackground,
                borderColor: secondaryBorder,
                color: secondaryText,
            }
        });

        const disabledSecondaryClass = mergeStyles({
            ...BASE_STYLE,
            color: disabledText,
            borderColor: disabledBorder,
            backgroundColor: disabledBackground,
            cursor: 'default',
        });

        const disabledPrimaryClass = mergeStyles({
            ...BASE_STYLE,
            color: disabledText,
            borderColor: disabledBorder,
            backgroundColor: disabledBackground,
            cursor: 'default',
        });

        const segmentedClasses = props.options.map((_, index) => mergeStyles(
            index === 0
                ? SEGMENTED_LEFT_STYLE
                : index === props.options.length - 1
                    ? SEGMENTED_RIGHT_STYLE
                    : SEGMENTED_MIDDLE_STYLE
        ));

        return {
            primaryClass,
            secondaryClass,
            disabledSecondaryClass,
            disabledPrimaryClass,
            segmentedClasses,
        };
    }, [theme, props.options]);

    const StylesByOption = useMemo(() => {
        const styles = props.options.map((_, index) => {
            if (index === 0) {
                return { root: { ...SEGMENTED_LEFT_STYLE, flexBasis: 0, flex: 1 } };
            } else if (index === props.options.length - 1) {
                return { root: { ...SEGMENTED_RIGHT_STYLE, flexBasis: 0, flex: 1 } };
            } else {
                return { root: { ...SEGMENTED_MIDDLE_STYLE, flexBasis: 0, flex: 1 } };
            }
        });
        return styles;
    }, [props.options]);


    const onSelectOption = useCallback((
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        option: IToggleOption<T>
    ) => {
        if (props.disabled) {
            return;
        }

        if (option.disabled)
            return;

        setSelectedKey(option.key);
        props.onChange?.(event, option);
    }, [props.disabled, props.onChange]);

    const onClearSelection = useCallback(
        (event: any) => {
            if (props.disabled || props.clearDisabled) {
                return;
            }

            setSelectedKey(null);
            props.onChange?.(event, undefined);
        }
        , [props.disabled, props.clearDisabled, props.onChange]
    );

    return (
        <div style={ROOT_STYLE}>

            <Label>
                {props.label}
            </Label>


            <Stack
                horizontal
                horizontalAlign='center'
                verticalAlign='center'
                tokens={{ childrenGap: 0 }}
                styles={{ root: { maxWidth: '100%' } }}
                className={props.optionsClassName}
            >

                {props.options.map((option, index) => {
                    if (option.hidden)
                        return <React.Fragment key={option.key}></React.Fragment>

                    const isSelected = option.key === selectedOption?.key;


                    let className = optionClasses.secondaryClass;

                    if (isSelected) {
                        if (props.disabled) {
                            className = optionClasses.disabledPrimaryClass;
                        } else {
                            className = optionClasses.primaryClass;
                        }
                    } else {
                        if (props.disabled || option.disabled) {
                            className = optionClasses.disabledSecondaryClass;
                        } else {
                            className = optionClasses.secondaryClass;
                        }
                    }

                    return (
                        <Stack.Item
                            key={option.key}
                            grow={1}

                            tabIndex={props.disabled ? undefined : 0}
                            styles={props.styles ?? StylesByOption[index] ?? DEFAULT_ITEM_STYLE}
                            className={className}
                            // className={mergeStyles(className, props.unclick ? optionClasses.secondaryClass : undefined, optionClasses.segmentedClasses[index])}
                            onClick={(event) => {
                                onSelectOption(event, option);
                            }}
                            onKeyDown={(event) => {
                                if (event.code === 'Space' || event.code === 'Enter') {
                                    event.preventDefault();
                                    onSelectOption(event, option);
                                }
                            }}
                        >
                            <div style={OPTION_TEXT_STYLE} className={'toggle-option ' + (isSelected ? 'selected' : '')}>
                                {isSelected && !props.unclick &&
                                    <span style={SELECTED_ARROW_STYLE}>
                                        ➤
                                    </span>
                                }
                                {option.text}
                            </div>
                        </Stack.Item>
                    );
                })}

                {!(props.clearHidden || props.clearDisabled) && (
                    <div style={CLEAR_CONTAINER_STYLE}>
                        <IconButton
                            iconProps={CLEAR_ICON_PROPS}
                            styles={CLEAR_BUTTON_STYLE}
                            disabled={props.disabled || props.clearDisabled}
                            onClick={onClearSelection}
                        />
                    </div>
                )}
            </Stack>
        </div>
    );
}
const MemoizedToggleOptions = React.memo(ToggleOptionsControl) as typeof ToggleOptionsControl;

export default MemoizedToggleOptions;